// ─── Faits techniques / IVV (Core) ────────────────────────────────
// Sous-ensemble VITAL du service MoPCal : créer un fait technique et lister les
// siens. Le workflow admin (analyse, affectation, clôture…) reste dans MoPCal.
// Base : `dbApp` (Firestore nommée « mopcal-app ») — MÊME collection que MoPCal,
// donc une remontée faite depuis une app de la suite arrive dans le même flux.
// Règles : create autorisé à tout compte connecté (auteurUid == uid) ; lire les
// SIENS ne demande pas le rôle IVV (seule la vue globale l'exige).
import {
  collection, doc, addDoc, onSnapshot, query, where, runTransaction, getDoc,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { dbApp as db } from '../firebase';

// ─── Référentiels (identiques à MoPCal) ───────────────────────────
export const ETATS_FT = {
  submitted:    { label: 'Soumis',        couleur: '#2563EB', bg: '#EFF6FF', ordre: 1 },
  need_info:    { label: 'Info demandée', couleur: '#D97706', bg: '#FEF3C7', ordre: 2 },
  analysed:     { label: 'Analysé',       couleur: '#7C3AED', bg: '#F5F3FF', ordre: 3 },
  assigned:     { label: 'Affecté',       couleur: '#0891B2', bg: '#ECFEFF', ordre: 4 },
  implemented:  { label: 'Implémenté',    couleur: '#059669', bg: '#ECFDF5', ordre: 5 },
  validated:    { label: 'Validé',        couleur: '#16A34A', bg: '#F0FDF4', ordre: 6 },
  closed:       { label: 'Clôturé',       couleur: '#6B7280', bg: '#F3F4F6', ordre: 7 },
  rejected:     { label: 'Rejeté',        couleur: '#DC2626', bg: '#FEF2F2', ordre: 8 },
  re_submitted: { label: 'Re-soumis',     couleur: '#2563EB', bg: '#EFF6FF', ordre: 9 },
  postponed:    { label: 'Reporté',       couleur: '#92400E', bg: '#FEF3C7', ordre: 10 },
};
export const etatInfo = (s) => ETATS_FT[s] || ETATS_FT.submitted;

export const GRAVITES = [
  { id: 'bloquant',  label: 'Bloquant'  },
  { id: 'majeur',    label: 'Majeur'    },
  { id: 'mineur',    label: 'Mineur'    },
  { id: 'remarque',  label: 'Remarque'  },
];
export const TYPES_FT = [
  { id: 'defaut',    label: 'Défaut'    },
  { id: 'evolution', label: 'Évolution' },
];
export const CATEGORIES_FT = [
  { id: 'ihm',            label: 'IHM'            },
  { id: 'fonctionnalite', label: 'Fonctionnalité' },
  { id: 'autre',          label: 'Autre'          },
];
export const NIVEAUX_CONFIANCE = ['Faible', 'Moyen', 'Élevé'];
export const PLATEFORMES_FT = ['iOS', 'Android', 'Web'];

// Chaque fait technique porte DEUX traces : `application` (d'où l'on remonte)
// et `module` (ce qui est concerné) → on sait toujours qui remonte quoi.
export { MODULES, moduleInfo, sousModulesDe, sousModuleInfo, APPS, appInfo, modulesDeApp } from '../modules';

export function plateformeDefaut() {
  if (Platform.OS === 'ios') return 'iOS';
  if (Platform.OS === 'android') return 'Android';
  return 'Web';
}

// ─── Numérotation (transaction sur config/compteurs.faitTechnique) ─
async function prochainNumero() {
  const ref = doc(db, 'config', 'compteurs');
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const courant = snap.exists() ? (snap.data().faitTechnique || 0) : 0;
    const suivant = courant + 1;
    if (snap.exists()) tx.update(ref, { faitTechnique: suivant });
    else tx.set(ref, { faitTechnique: 1 });
    return suivant;
  });
}

// ─── Création ─────────────────────────────────────────────────────
export async function creerFait({
  auteurUid, auteurNom, titre, contexte = '', etapes = '',
  gravite = 'mineur', type = 'defaut', categorie = '',
  comportementAttendu = '', niveauConfiance = 'Moyen',
  pageProb = '', fonctionnalite = '', captures = [],
  appVersion = '', plateforme,
  application = '',            // ← app d'où l'on remonte : mop-empty | mop-cal | …
  module = '',                 // ← module CONCERNÉ : core | empty | cal | inventory | finance
  sousModule = '',             // ← sous-module (ex. core-connexion, core-ivv)
}) {
  const t = (titre || '').trim();
  if (!t) throw new Error('Titre requis');
  if (!auteurUid) throw new Error('Utilisateur requis');
  const numero = await prochainNumero();
  const now = new Date().toISOString();
  const ref = await addDoc(collection(db, 'faitsTechniques'), {
    numero,
    statut: 'submitted',
    priorite: null,
    auteurUid,
    auteurNom: auteurNom || '',
    titre: t,
    contexte: (contexte || '').trim(),
    etapes: (etapes || '').trim(),
    gravite, type, categorie,
    comportementAttendu: (comportementAttendu || '').trim(),
    niveauConfiance,
    pageProb, fonctionnalite,
    captures: Array.isArray(captures) ? captures : [],
    appVersion,
    application,                              // app d'où vient la remontée
    module,                                   // module fonctionnel concerné
    sousModule,                               // sous-module (fonctionnalité précise)
    plateforme: plateforme || plateformeDefaut(),
    creeLe: now,
    historique: [{ de: null, vers: 'submitted', par: auteurUid, parNom: auteurNom || '', le: now, note: '' }],
  });
  return ref.id;
}

// ─── Mes faits techniques (tri numéro décroissant) ────────────────
// CLOISON PAR APPLICATION : une app de la suite ne voit QUE ses propres
// remontées. Les FT de MoPCal (qui ne portent pas encore le champ
// `application`) n'apparaissent donc pas dans mop-empty, et réciproquement.
// Deux clauses d'égalité → Firestore les sert sans index composite.
export function ecouterMesFaits(uid, application, callback) {
  if (typeof application === 'function') { callback = application; application = ''; }
  if (!uid) { callback([]); return () => {}; }
  const clauses = [where('auteurUid', '==', uid)];
  if (application) clauses.push(where('application', '==', application));
  const q = query(collection(db, 'faitsTechniques'), ...clauses);
  return onSnapshot(
    q,
    (snap) => {
      const liste = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(f => !application || f.application === application);
      liste.sort((a, b) => (b.numero || 0) - (a.numero || 0));
      callback(liste);
    },
    () => callback([]),
  );
}

// Lecture ponctuelle d'un fait (utile pour un détail).
export async function lireFait(id) {
  const snap = await getDoc(doc(db, 'faitsTechniques', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
